<!--
  DevicesView.vue — device-settings screen (route /devices).
  The Tier-2 schema portlet: pick a device, fetch its server-authored JSON Schema
  (tracker/settings/tracking/schema/read) and render it through the schema-form engine
  (Engine A). The save endpoint is named INSIDE the schema (schema.apiMethod), so new
  config screens ship server-side with zero frontend code. Reached from the row-action
  "Open device settings", the NavRail, and the widgets' "Setup odometer/engine-hours"
  links (which pass ?tracker=<id> / ?setup=<counter>).
  Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
  (module `device-settings`, endpoint `tracker/settings/tracking/schema/read`,
  schema.apiMethod save — DEVICE-SETTINGS.md), internal impl re-authored.
-->
<template>
  <v-container fluid>
    <v-toolbar flat dense color="transparent">
      <v-toolbar-title>Device settings</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-chip v-if="$route.query.setup" small color="blue" text-color="white">setup: {{ $route.query.setup }}</v-chip>
    </v-toolbar>
    <v-divider></v-divider>

    <v-layout row wrap class="mt-2">
      <!-- device list -->
      <v-flex xs12 sm4 md3>
        <v-card class="pa-1">
          <v-text-field v-model="q" flat solo-inverted hide-details prepend-inner-icon="search" placeholder="Search device" class="ma-1"></v-text-field>
          <v-list dense class="ds-list">
            <v-list-tile v-for="t in filtered" :key="t.id" :class="{ 'ds-sel': sel && sel.id === t.id }" @click="select(t)">
              <v-list-tile-content>
                <v-list-tile-title>{{ t.label || ('#' + t.id) }}</v-list-tile-title>
                <v-list-tile-sub-title class="tracko-mono">{{ (t.source && t.source.model) || '—' }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <div v-if="!filtered.length" class="tracko-muted pa-3">No devices.</div>
          </v-list>
        </v-card>
      </v-flex>

      <!-- schema portlet -->
      <v-flex xs12 sm8 md9>
        <v-card class="pa-3">
          <div v-if="!sel" class="tracko-muted pa-3">Select a device to edit its settings.</div>
          <div v-else-if="loading" class="tracko-center pa-5">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <schema-form
            v-else
            :key="sel.id"
            :schema="schema"
            :value="value"
            :tracker-id="sel.id"
            save-label="Save settings"
            @saved="onSaved"
          ></schema-form>
          <v-alert :value="saved" type="success" outline dismissible @input="saved = false">Settings saved.</v-alert>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { request } from '@/api/request'
import SchemaForm from '@/entity/SchemaForm.vue'

export default {
  name: 'DevicesView',
  components: { 'schema-form': SchemaForm },
  data() {
    return { trackers: [], q: '', sel: null, schema: {}, value: {}, loading: false, saved: false }
  },
  computed: {
    filtered() {
      const q = this.q.trim().toLowerCase()
      if (!q) return this.trackers
      return this.trackers.filter((t) => ((t.label || '') + ' ' + t.id).toLowerCase().indexOf(q) !== -1)
    }
  },
  created() {
    request('tracker/list', {}, 'list').then((list) => {
      this.trackers = list || []
      // preselect: ?tracker=<id> (from a widget/row deep-link) or the first device.
      const wantId = this.$route.query.tracker
      const first = (wantId && this.trackers.find((t) => String(t.id) === String(wantId))) || this.trackers[0]
      if (first) this.select(first)
    }).catch(() => { /* backend/session unavailable */ })
  },
  methods: {
    async select(t) {
      this.sel = t
      this.saved = false
      this.loading = true
      try {
        const r = await request('tracker/settings/tracking/schema/read', { tracker_id: t.id })
        this.schema = (r && r.schema) || {}
        this.value = (r && r.value) || {}
      } catch (e) {
        this.schema = {}
        this.value = {}
      } finally {
        this.loading = false
      }
    },
    onSaved() { this.saved = true }
  }
}
</script>

<style scoped>
.ds-list { max-height: 70vh; overflow-y: auto; }
.ds-sel { background: #e3f1f8; }
</style>
